import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  useTheme,
  Chip,
  TextField,
  DialogContentText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { alpha } from "@mui/material/styles";
import MergeRoundedIcon from "@mui/icons-material/MergeRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import Overlay from "components/Overlay/Overlay";

const ClientMergeOverlay = () => {
  const theme = useTheme();

  const {
    overlayData,
    setOverlayName,
    triggerPageRefresh,
    showSnackbar,
    openDialog,
    closeDialog,
  } = useStateContext();
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const clients = useMemo(() => {
    return Array.isArray(overlayData?.Clients) ? overlayData.Clients : [];
  }, [overlayData?.Clients]);

  const clientById = useMemo(() => {
    return new Map(clients.map((client) => [Number(client.id), client]));
  }, [clients]);

  const selectedIds = useMemo(() => {
    const raw = overlayData?.SelectedClientIds || [];
    return Array.isArray(raw) ? raw.map(Number) : [];
  }, [overlayData?.SelectedClientIds]);

  const normalizeName = useCallback((value) => {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const damerauLevenshteinDistance = useCallback((a, b) => {
    const left = String(a || "");
    const right = String(b || "");

    const leftLength = left.length;
    const rightLength = right.length;

    if (leftLength === 0) return rightLength;
    if (rightLength === 0) return leftLength;

    const matrix = Array.from({ length: leftLength + 1 }, () =>
      new Array(rightLength + 1).fill(0),
    );

    for (let i = 0; i <= leftLength; i += 1) {
      matrix[i][0] = i;
    }

    for (let j = 0; j <= rightLength; j += 1) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= leftLength; i += 1) {
      for (let j = 1; j <= rightLength; j += 1) {
        const substitutionCost = left[i - 1] === right[j - 1] ? 0 : 1;

        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + substitutionCost,
        );

        if (
          i > 1 &&
          j > 1 &&
          left[i - 1] === right[j - 2] &&
          left[i - 2] === right[j - 1]
        ) {
          matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + 1);
        }
      }
    }

    return matrix[leftLength][rightLength];
  }, []);

  const normalizedSimilarity = useCallback((left, right) => {
    const a = String(left || "");
    const b = String(right || "");
    const maxLength = Math.max(a.length, b.length);

    if (!maxLength) return 1;

    const distance = damerauLevenshteinDistance(a, b);
    return Math.max(0, 1 - distance / maxLength);
  }, [damerauLevenshteinDistance]);

  const getNameMatchPercent = useCallback((baseName, candidateName) => {
    const base = normalizeName(baseName);
    const candidate = normalizeName(candidateName);

    if (!base || !candidate) return 0;
    if (base === candidate) return 100;

    const compactBase = base.replace(/\s+/g, "");
    const compactCandidate = candidate.replace(/\s+/g, "");
    const fullNameScore = normalizedSimilarity(compactBase, compactCandidate);

    const baseTokens = base.split(" ").filter(Boolean);
    const candidateTokens = candidate.split(" ").filter(Boolean);

    const tokenLengthsTotal = baseTokens.reduce(
      (sum, token) => sum + token.length,
      0,
    );

    const tokenScore =
      tokenLengthsTotal > 0
        ? baseTokens.reduce((sum, token) => {
            const bestTokenSimilarity = candidateTokens.reduce(
              (best, otherToken) =>
                Math.max(best, normalizedSimilarity(token, otherToken)),
              0,
            );
            return sum + bestTokenSimilarity * token.length;
          }, 0) / tokenLengthsTotal
        : 0;

    const firstNameScore = normalizedSimilarity(
      baseTokens[0] || "",
      candidateTokens[0] || "",
    );
    const lastNameScore = normalizedSimilarity(
      baseTokens[baseTokens.length - 1] || "",
      candidateTokens[candidateTokens.length - 1] || "",
    );

    const finalScore =
      fullNameScore * 0.55 +
      tokenScore * 0.2 +
      firstNameScore * 0.1 +
      lastNameScore * 0.15;

    return Math.max(0, Math.min(100, Math.round(finalScore * 100)));
  }, [normalizeName, normalizedSimilarity]);

  const initialMergeIds = useMemo(() => {
    if (!selectedIds.length) return [];
    return selectedIds;
  }, [selectedIds]);

  const initialOtherIds = useMemo(() => {
    if (initialMergeIds.length === 0) return [];

    const mergeSet = new Set(initialMergeIds);
    const keepClientId = Number(initialMergeIds[0]);
    const keepClient = clientById.get(keepClientId);
    const keepAccountName = keepClient?.Account || "";
    const keepShortName = keepClient?.ShortName || "";

    return clients
      .map((client) => Number(client.id))
      .filter((id) => !mergeSet.has(id))
      .map((id, index) => {
        const client = clientById.get(id);
        const shortName = client?.ShortName || "";
        
        const accountScore = getNameMatchPercent(keepAccountName, client?.Account);
        const shortNameScore = getNameMatchPercent(keepShortName, shortName);

        return {
          id,
          index,
          accountScore,
          shortNameScore,
        };
      })
      .sort((a, b) => {
        // Sort by account score first, then short-name score, then by original index
        if (b.accountScore !== a.accountScore) return b.accountScore - a.accountScore;
        if (b.shortNameScore !== a.shortNameScore) {
          return b.shortNameScore - a.shortNameScore;
        }
        return a.index - b.index;
      })
      .map((item) => item.id);
  }, [getNameMatchPercent, initialMergeIds, clientById, clients]);

  const [clientsToMergeIds, setClientsToMergeIds] = useState(initialMergeIds);
  const [otherClientIds, setOtherClientIds] = useState(initialOtherIds);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [otherSearch, setOtherSearch] = useState("");

  const filteredOtherClientIds = useMemo(() => {
    const query = String(otherSearch || "").trim().toLowerCase();
    if (!query) return otherClientIds;

    return otherClientIds.filter((clientId) => {
      const client = clientById.get(Number(clientId));
      if (!client) return false;

      const account = String(client.Account || "").toLowerCase();
      const shortName = String(client.ShortName || "").toLowerCase();
      return account.includes(query) || shortName.includes(query);
    });
  }, [otherSearch, otherClientIds, clientById]);

  const keptAccountName = useMemo(() => {
    const keepId = Number(clientsToMergeIds[0]);
    return clientById.get(keepId)?.Account || "";
  }, [clientById, clientsToMergeIds]);

  const keptShortName = useMemo(() => {
    const keepId = Number(clientsToMergeIds[0]);
    const client = clientById.get(keepId);
    return client?.ShortName || "";
  }, [clientById, clientsToMergeIds]);

  // Lightweight client-side preview of what will move to the kept client
  // (counts from the already-loaded list rows — no server round-trip).
  const mergePreview = useMemo(() => {
    if (clientsToMergeIds.length < 2) return null;
    const keepId = Number(clientsToMergeIds[0]);
    const keep = clientById.get(keepId);
    const mergeClients = clientsToMergeIds
      .slice(1)
      .map((id) => clientById.get(Number(id)))
      .filter(Boolean);

    let requestors = 0;
    let serviceTypes = 0;
    let paymentMethods = 0;
    let contractHistory = 0;
    mergeClients.forEach((c) => {
      requestors += c.Requestors?.length || 0;
      serviceTypes += c.ClientServiceTypes?.length || 0;
      paymentMethods += c.ClientPaymentMethods?.length || 0;
      contractHistory += c.ClientContractHistory?.length || 0;
    });

    return {
      keepName: keep?.Account || "the first client",
      mergeCount: mergeClients.length,
      requestors,
      serviceTypes,
      paymentMethods,
      contractHistory,
    };
  }, [clientsToMergeIds, clientById]);

  const moveClientToMerge = (clientId, targetIndex) => {
    const clientIdNumber = Number(clientId);
    if (!otherClientIds.includes(clientIdNumber)) return;

    const nextOther = otherClientIds.filter((id) => id !== clientIdNumber);
    const safeIndex =
      typeof targetIndex === "number"
        ? Math.max(0, Math.min(targetIndex, clientsToMergeIds.length))
        : clientsToMergeIds.length;

    const nextMerge = [...clientsToMergeIds];
    nextMerge.splice(safeIndex, 0, clientIdNumber);

    setClientsToMergeIds(nextMerge);
    setOtherClientIds(nextOther);
  };

  const sortOtherClientIdsByMatch = useCallback(
    (ids, mergeIds) => {
      const keepId = Number((mergeIds || [])[0]);
      const keepClient = clientById.get(keepId);
      const keepAccountName = keepClient?.Account || "";
      const keepShortName = keepClient?.ShortName || "";

      return [...ids]
        .map((id, index) => {
          const client = clientById.get(Number(id));
          const shortName = client?.ShortName || "";

          return {
            id,
            index,
            accountScore: getNameMatchPercent(keepAccountName, client?.Account),
            shortNameScore: getNameMatchPercent(keepShortName, shortName),
          };
        })
        .sort((a, b) => {
          if (b.accountScore !== a.accountScore) return b.accountScore - a.accountScore;
          if (b.shortNameScore !== a.shortNameScore) {
            return b.shortNameScore - a.shortNameScore;
          }
          return a.index - b.index;
        })
        .map((item) => item.id);
    },
    [clientById, getNameMatchPercent],
  );

  const moveClientToOther = (clientId, targetIndex) => {
    const clientIdNumber = Number(clientId);
    if (!clientsToMergeIds.includes(clientIdNumber)) return;

    const nextMerge = clientsToMergeIds.filter((id) => id !== clientIdNumber);
    const nextOther = [...otherClientIds];
    if (!nextOther.includes(clientIdNumber)) {
      nextOther.push(clientIdNumber);
    }

    const sortedOther = sortOtherClientIdsByMatch(nextOther, nextMerge);

    setClientsToMergeIds(nextMerge);
    setOtherClientIds(sortedOther);
  };

  const handleDragEnd = (result) => {
    setIsDragging(false);

    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    if (source.droppableId === "merge-clients") {
      const mergeSource = [...clientsToMergeIds];
      const [movedId] = mergeSource.splice(source.index, 1);

      if (destination.droppableId === "merge-clients") {
        mergeSource.splice(destination.index, 0, movedId);
        setClientsToMergeIds(mergeSource);
      } else {
        const nextOther = [...otherClientIds, movedId];
        const sortedOther = sortOtherClientIdsByMatch(nextOther, mergeSource);
        setClientsToMergeIds(mergeSource);
        setOtherClientIds(sortedOther);
      }
      return;
    }

    const otherSource = [...otherClientIds];
    const [movedId] = otherSource.splice(source.index, 1);

    if (destination.droppableId === "other-clients") {
      otherSource.splice(destination.index, 0, movedId);
      setOtherClientIds(otherSource);
    } else {
      const nextMerge = [...clientsToMergeIds];
      nextMerge.splice(destination.index, 0, movedId);
      setClientsToMergeIds(nextMerge);
      setOtherClientIds(otherSource);
    }
  };

  const mergeClients = async () => {
    if (clientsToMergeIds.length < 2) {
      showSnackbar(
        "Select at least 2 clients in 'Clients to Merge'. The first one is kept.",
        theme,
        "error",
      );
      return;
    }

    const keepClientId = Number(clientsToMergeIds[0]);
    const mergeClientIds = clientsToMergeIds.slice(1).map(Number);

    try {
      setIsSaving(true);

      const response = await api.post("/clients/mergeClients", {
        data: {
          keepClientId,
          mergeClientIds,
        },
      });

      const movedServiceLinksCount =
        Number(response?.data?.movedServiceLinksCount) || 0;
      const movedInvoiceLinksCount =
        Number(response?.data?.movedInvoiceLinksCount) || 0;
      const movedRequestorLinksCount =
        Number(response?.data?.movedRequestorLinksCount) || 0;

      if (socket) {
        socket.emit("entity:saved", {
          modelType: "ClientsList",
          recordId: "all",
          changeType: "Client",
          changeAction: "Merged",
          user: {
            id: currentUser?.id,
            name: currentUser?.Name || "User",
            imageThumbnail: currentUser?.ImageThumbnail,
          },
        });
      }

      triggerPageRefresh(
        `Clients merged successfully. Re-linked ${movedServiceLinksCount} service(s), ${movedInvoiceLinksCount} invoice(s), and ${movedRequestorLinksCount} requestor(s).`,
        theme,
      );

      closeDialog();
      setOverlayName("");
    } catch (error) {
      const statusCode = error?.response?.status;
      const baseMessage =
        error?.response?.data?.error ||
        "An error occurred while merging clients.";
      const message = statusCode
        ? `${baseMessage} (HTTP ${statusCode})`
        : baseMessage;
      closeDialog();
      showSnackbar(message, theme, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMergeClick = () => {
    if (clientsToMergeIds.length < 2) {
      showSnackbar(
        "Select at least 2 clients in 'Clients to Merge'. The first one is kept.",
        theme,
        "error",
      );
      return;
    }

    const mergedCount = clientsToMergeIds.length - 1;
    const keepName = keptAccountName || "the first client";
    const preview = mergePreview;

    const movedParts = [];
    if (preview) {
      if (preview.requestors > 0)
        movedParts.push(`${preview.requestors} requestor(s)`);
      if (preview.serviceTypes > 0)
        movedParts.push(`${preview.serviceTypes} service type link(s)`);
      if (preview.paymentMethods > 0)
        movedParts.push(`${preview.paymentMethods} payment method link(s)`);
      if (preview.contractHistory > 0)
        movedParts.push(`${preview.contractHistory} contract history row(s)`);
    }

    openDialog({
      theme,
      title: "Merge Clients?",
      message: `Merge ${mergedCount} client${mergedCount === 1 ? "" : "s"} into "${keepName}"? This cannot be undone.`,
      customContent: (
        <Box>
          <DialogContentText>
            {`Merge ${mergedCount} client${mergedCount === 1 ? "" : "s"} into "${keepName}"? This cannot be undone.`}
          </DialogContentText>
          {preview && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: "12px",
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.primary.border2}`,
              }}
            >
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                Merge preview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {movedParts.length > 0
                  ? `Will move to "${preview.keepName}": ${movedParts.join(", ")}. `
                  : "No requestors, service types, payment methods, or contract history found on the merged client(s). "}
                Services, invoices, groups, and history are re-linked; duplicate
                links are skipped.
              </Typography>
            </Box>
          )}
        </Box>
      ),
      primaryAction: "Merge",
      secondaryAction: "Cancel",
      primaryOnClick: mergeClients,
      secondaryOnClick: closeDialog,
    });
  };

  const renderClientCard = (clientId, index, listType) => {
    const client = clientById.get(Number(clientId));
    if (!client) return null;

    const isMergeList = listType === "merge";
    const isPrimaryKept = isMergeList && index === 0;

    const backgroundColor = isPrimaryKept
      ? alpha(theme.palette.primary.main, 0.12)
      : isMergeList
      ? alpha(theme.palette.warning.main, 0.12)
      : theme.palette.background.default;

    const borderColor = isPrimaryKept
      ? theme.palette.primary.main
      : isMergeList
      ? theme.palette.warning.main
      : theme.palette.primary.border2;

    const chipLabel = isPrimaryKept ? "Keep" : isMergeList ? "Merge" : "Other";
    
    const accountMatchPercent = isMergeList
      ? null
      : getNameMatchPercent(keptAccountName, client.Account);
    
    const shortName = client?.ShortName || "";
    const shortNameMatchPercent = isMergeList
      ? null
      : getNameMatchPercent(keptShortName, shortName);

    return (
      <Box
        key={`client-${client.id}`}
        sx={{
          mb: 1,
          p: 1.25,
          borderRadius: "12px",
          bgcolor: backgroundColor,
          border: `1px solid ${borderColor}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: theme.palette.text.secondary,
                cursor: "grab",
              }}
            >
              <DragIndicatorRoundedIcon fontSize="small" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" fontWeight={700} noWrap>
                {client.Account || "Unnamed Client"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {shortName || "No Short Name"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Chip
              label={chipLabel}
              size="small"
              sx={{
                bgcolor: isPrimaryKept
                  ? theme.palette.primary.main
                  : isMergeList
                  ? theme.palette.warning.main
                  : theme.palette.background.alt,
                color:
                  isPrimaryKept || isMergeList
                    ? theme.palette.background.default
                    : theme.palette.text.primary,
                fontWeight: 700,
              }}
            />
            {isMergeList ? (
              <IconButton
                size="small"
                onClick={() => moveClientToOther(client.id)}
                aria-label="Remove from merge list"
              >
                <RemoveRoundedIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                onClick={() => moveClientToMerge(client.id)}
                aria-label="Add to merge list"
              >
                <AddRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          Type: {client.Type || "N/A"}
        </Typography>
        {!isMergeList && (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Account Match: {accountMatchPercent}%
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Short Name Match: {shortNameMatchPercent}%
            </Typography>
          </>
        )}
      </Box>
    );
  };

  return (
    <Overlay
      id="client-merge"
      open={true}
      onClose={() => setOverlayName("")}
      title="Merge Clients"
      subtitle="Drag clients between lists or use +/- buttons. In the left list, the first client is kept and the rest are merged into it."
      maxWidthPx={1600}
      height="90vh"
      contentBg="default"
      scrollable={false}
      footer={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", justifyContent: "flex-end" }}>
          <Button
            color="primary"
            variant="outlined"
            size="large"
            endIcon={<DisabledByDefaultRoundedIcon />}
            onClick={() => setOverlayName("")}
            sx={{ borderRadius: "12px" }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            size="large"
            endIcon={<MergeRoundedIcon />}
            disabled={isSaving || clientsToMergeIds.length === 0}
            onClick={handleMergeClick}
            sx={{ borderRadius: "12px" }}
          >
            Merge
          </Button>
        </Box>
      }
    >
        <Box
          sx={{
            display: "flex",
            p: "1rem 1.5rem",
            width: "100%",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <DragDropContext
            onDragStart={() => setIsDragging(true)}
            onDragEnd={handleDragEnd}
          >
            <Grid
              container
              spacing={2}
              sx={{ width: "100%", minHeight: 0, height: "100%" }}
            >
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ minHeight: 0, height: { xs: "auto", md: "100%" }, display: "flex" }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: { xs: "auto", md: "100%" },
                  minHeight: 0,
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.primary.border2}`,
                  bgcolor: theme.palette.background.default,
                  p: 1.25,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                  Clients to Merge
                </Typography>
                <Droppable droppableId="merge-clients" type="CLIENT">
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        height: { xs: "30vh", md: "100%" },
                        maxHeight: { xs: "30vh", md: "100%" },
                        overflowY: "auto",
                        borderRadius: "12px",
                        p: 0.5,
                        bgcolor:
                          snapshot.isDraggingOver || isDragging
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                      }}
                    >
                      {clientsToMergeIds.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          Add at least two clients here to merge.
                        </Typography>
                      ) : (
                        clientsToMergeIds.map((clientId, index) => (
                          <Draggable key={clientId} draggableId={String(clientId)} index={index}>
                            {(drag) => (
                              <Box
                                ref={drag.innerRef}
                                {...drag.draggableProps}
                                {...drag.dragHandleProps}
                              >
                                {renderClientCard(clientId, index, "merge")}
                              </Box>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Box>
            </Grid>

            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ minHeight: 0, height: { xs: "auto", md: "100%" }, display: "flex" }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: { xs: "auto", md: "100%" },
                  minHeight: 0,
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.primary.border2}`,
                  bgcolor: theme.palette.background.default,
                  p: 1.25,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                  Other Clients
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search other clients"
                  value={otherSearch}
                  onChange={(event) => setOtherSearch(event.target.value)}
                  sx={{ mb: 1 }}
                />
                <Droppable droppableId="other-clients" type="CLIENT">
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        height: { xs: "30vh", md: "100%" },
                        maxHeight: { xs: "30vh", md: "100%" },
                        overflowY: "auto",
                        borderRadius: "12px",
                        p: 0.5,
                        bgcolor:
                          snapshot.isDraggingOver || isDragging
                            ? alpha(theme.palette.warning.main, 0.1)
                            : "transparent",
                      }}
                    >
                      {otherClientIds.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          No clients remaining.
                        </Typography>
                      ) : filteredOtherClientIds.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          No clients match your search.
                        </Typography>
                      ) : (
                        filteredOtherClientIds.map((clientId, index) => (
                          <Draggable key={clientId} draggableId={String(clientId)} index={index}>
                            {(drag) => (
                              <Box
                                ref={drag.innerRef}
                                {...drag.draggableProps}
                                {...drag.dragHandleProps}
                              >
                                {renderClientCard(clientId, index, "other")}
                              </Box>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Box>
            </Grid>
            </Grid>
          </DragDropContext>
        </Box>
    </Overlay>
  );
};

export default ClientMergeOverlay;
